import { useCallback, useEffect, useState } from "react";
import { getKYCAccessToken } from "../../../api/kyc";
import { TokenType } from "../../../constants";
import { toast } from "../../../toasts";

// Base message structure
interface SumSubMessage {
  type: string;
  [key: string]: unknown;
}

// Specific message types based on SumSub documentation
interface ApplicantStatusChangedMessage extends SumSubMessage {
  type: "idCheck.onApplicantStatusChanged";
  reprocessing: boolean;
  levelName: string;
  createDate: string;
  expireDate: string;
  reviewStatus: string;
  autoChecked: boolean;
}

interface StepInitiatedMessage extends SumSubMessage {
  type: "idCheck.onStepInitiated";
  idDocSetType: string;
  types: string[];
}

interface ApplicantLoadedMessage extends SumSubMessage {
  type: "idCheck.onApplicantLoaded";
  applicantId: string;
}

interface StepCompletedMessage extends SumSubMessage {
  type: "idCheck.onStepCompleted";
  idDocSetType: string;
}

interface LivenessCompletedMessage extends SumSubMessage {
  type: "idCheck.onLivenessCompleted";
  answer: string;
  allowContinuing: boolean;
}

interface UploadErrorMessage extends SumSubMessage {
  type: "idCheck.onUploadError";
  code: string;
  msg: string;
}

interface ResizeMessage extends SumSubMessage {
  type: "idCheck.onResize";
  height: number;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type SumSubMessages =
  | ApplicantStatusChangedMessage
  | StepInitiatedMessage
  | ApplicantLoadedMessage
  | StepCompletedMessage
  | LivenessCompletedMessage
  | UploadErrorMessage
  | ResizeMessage
  | SumSubMessage; // Fallback for unknown message types

interface UseKycParams {
  handleModalClose?: () => void;
  handleModalOpen?: () => void;
  onboardingVariant?: boolean;
  notMountListener?: boolean;
  initialVerificationType?: TokenType;
}

export const useKYC = ({
  handleModalClose,
  handleModalOpen,
  onboardingVariant,
  notMountListener,
  initialVerificationType = TokenType.INDIVIDUAL,
}: UseKycParams = {}) => {
  const [inProgressInit, setInProgressInit] = useState(false);
  const [accessToken, setAccessToken] = useState("");
  const [currentVerificationType, setCurrentVerificationType] =
    useState<TokenType>(initialVerificationType);

  const config = {
    lang: "en",
  };

  const options = {
    addViewportTag: false,
    adaptIframeHeight: true,
  };

  // Reset accessToken when verification type changes
  useEffect(() => {
    if (currentVerificationType !== initialVerificationType) {
      setAccessToken("");
      setCurrentVerificationType(initialVerificationType);
    }
  }, [initialVerificationType]);

  const fetchAccessToken = async (tokenType: TokenType) => {
    if (inProgressInit) return;
    setInProgressInit(true);
    try {
      // Clear any previous token when changing types
      if (tokenType !== currentVerificationType) {
        setAccessToken("");
      }

      setCurrentVerificationType(tokenType);
      const { data } = await getKYCAccessToken(tokenType);
      setAccessToken(data.accessToken);
      if (handleModalOpen && !onboardingVariant) handleModalOpen();

      return data.accessToken;
    } catch (e: unknown) {
      toast.error(
        "Verification Error",
        "Failed to initiate verification. Please try again later."
      );
    } finally {
      setInProgressInit(false);
    }
  };

  const accessTokenExpirationHandler = useCallback(async () => {
    const newToken = await fetchAccessToken(currentVerificationType);
    return newToken || "";
  }, [currentVerificationType]);

  // const isStepInitiated = (
  //   message: SumSubMessage
  // ): message is StepInitiatedMessage =>
  //   message.type === "idCheck.onStepInitiated";

  // const isApplicantLoaded = (
  //   message: SumSubMessage
  // ): message is ApplicantLoadedMessage =>
  //   message.type === "idCheck.onApplicantLoaded";

  const errorHandler = useCallback((error: unknown) => {
    toast.error(
      error && typeof error === "object" && "code" in error
        ? String(error.code)
        : "Verification Error",
      error && typeof error === "object" && "error" in error
        ? String(error.error)
        : "An unknown error occurred"
    );
    if (handleModalClose) handleModalClose();
  }, []);

  useEffect(() => {
    if (!notMountListener && handleModalOpen && !accessToken) {
      fetchAccessToken(initialVerificationType);
    }
  }, []);

  return {
    accessToken,
    fetchAccessToken,
    accessTokenExpirationHandler,
    config,
    options,
    errorHandler,
    inProgressInit,
    currentVerificationType,
    setAccessToken, // Expose this so we can reset the token when needed
  };
};
